import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import CustomDate from '../../Common/customComponents/CustomDate.tsx';
import { commonMsg, dateUtil } from '../../../utils/globalUtil.tsx';
import { StyledMyIssue } from './style.ts';
import { TextButton } from '../../../components';
import CustomTable from '../../Common/customComponents/CustomTable.tsx';
import CustomSelectSingle from '../../Common/customComponents/CustomSelectSingle.tsx';
import CustomText from '../../Common/customComponents/CustomText.tsx';
import { useAsync } from '../../../hooks';
import PermitIssueTypeModal from './modal/PermitIssueTypeModal.tsx';
import PermitIssueDetailModal from './modal/PermitIssueDetailModal.tsx';
import { ptwApi } from '../../../api/ptw.api.ts';
import CustomButton from '../../Common/customComponents/CustomButton.tsx';
import { PermitIssueContext } from '../contexts/PermitIssueContext.ts';
import { usePtwDetailModal } from '../contexts/PtwDetailModalContext.ts';
import { useStatusUpdateDate } from '../contexts/PermitStatusUpdateDate.ts';
import { codeApi } from '../../../api/code.api.ts';

export const permitIssueTypeList = [
  {
    label: 'Cold',
    value: 'CW'
  },
  {
    label: 'Hot',
    value: 'HW'
  },
  {
    label: 'Confined Space',
    value: 'SW'
  },
  {
    label: 'Road Closure',
    value: 'RC'
  },
  {
    label: 'Excavation Clearance',
    value: 'EC'
  },
  {
    label: 'Lifting Plan',
    value: 'LP'
  }
];

export const getPermitIssueLabel = (value: string): string => {
  return permitIssueTypeList.find((x) => x.value === value)?.label as string;
};

// 검색조건 드롭다운에서는 더 이상 신규 발행되지 않는 Excavation Clearance/Lifting Plan 제외
export const searchablePermitTypeList = permitIssueTypeList.filter(
  (x) => x.value !== 'EC' && x.value !== 'LP'
);

const initSearchData = {
  startDt: dateUtil.dateByMonthStart(-3),
  endDt: dateUtil.dateByMonthEnd(0)
};

const PtwMyIssue = () => {
  /*일반*/
  const [searchData, setSearchData] = useState<any>(initSearchData);
  const { callAsync, showAlarm, closeAlarm } = useAsync();
  const [isPermitIssueTypeModalOpen, setIsPermitIssueTypeModalOpen] = useState<boolean>(false);
  const [permitIssueList, setPermitIssueList] = useState<string[]>([]);
  const { statusUpdateDate, setStatusUpdateDate } = useStatusUpdateDate();

  /*const [isPermitIssueDetailModalOpen, setIsPermitIssueDetailModalOpen] = useState<boolean>(false);*/
  const { isPtwDetailModalOpen, setIsPtwDetailModalOpen } = usePtwDetailModal();
  const [detailData, setDetailData] = useState<any>({});

  /*테이블관련*/
  const [gridData, setGridData] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [tableSort, setTableSort] = useState<any>({});
  const [pageSize, setPageSize] = useState<number>(10);
  const [selectedRow, setSelectedRow] = useState<any>({});
  const [checkedList, setCheckedList] = useState<any>([]);

  const [disciplineCdList, setDisciplineCdList] = useState<any>([]);

  const handleChange = (e: any) => {
    setSearchData((oData: any) => {
      return { ...oData, [e.target.name]: e.target.value };
    });
  };

  const isFirst = useRef(true);
  useEffect(() => {
    const t = setTimeout(() => {
      if (isFirst.current) {
        isFirst.current = false;
        return;
      }

      search(1);
    }, 100);

    return () => clearTimeout(t);
  }, [searchData, statusUpdateDate]);

  useEffect(() => {
    search(currentPage);
  }, [currentPage, tableSort.sortColumn, tableSort.sortValue, pageSize]);

  useEffect(() => {
    codeApi.getDiscpCodeList().then((list) => {
      setDisciplineCdList(list);
    });
  }, []);

  const search = (pageNum?: number) => {
    const param = {
      ...searchData,
      page: pageNum,
      size: pageSize,
      ...(tableSort?.sortColumn && tableSort?.sortValue
        ? { sort: `${tableSort.sortColumn},${tableSort.sortValue}` }
        : {})
    };

    ptwApi.getPtwIssueList(param).then(function (r) {
      setGridData(r.resultList);
      setTotalCount(r.resultList[0]?.totalCount);
    });
  };

  const columns = [
    {
      column: 'ptwNo',
      label: 'PTW No',
      align: 'center',
      sort: true,
      size: 150
    },
    {
      column: 'workRisk',
      label: 'Work Risk',
      align: 'center',
      size: 150,
      type: 'custom',
      custom: (row: any) => {
        return (
          <div className={'flex-row flex-row-just-center'}>
            {row.workRisk === 'GENERAL' && (
              <button className={'grid-button blue'}>General Work</button>
            )}
            {row.workRisk === 'HIGH' && (
              <CustomButton text={'High-Risk Work'} className={'form-button red'} />
            )}
          </div>
        );
      }
    },
    {
      column: 'companyNm',
      label: 'Company',
      align: 'center',
      size: 150
    },
    {
      column: 'formCode',
      label: 'Permit Type',
      align: 'center',
      sort: true,
      size: 150,
      type: 'custom',
      custom: (row: any) => {
        return permitIssueTypeList.find((x) => x.value === row.formCode)?.label as string;
      }
    },
    {
      column: 'disciplineNm',
      label: 'Discipline',
      align: 'center',
      size: 150
    },
    {
      column: 'workNm',
      label: 'Detail 1',
      align: 'center',
      size: 150
    },
    {
      column: 'taskNm',
      label: 'Detail 2',
      align: 'center',
      size: 150
    },
    {
      column: 'validDate',
      label: 'Valid Date',
      align: 'center',
      size: 300,
      type: 'custom',
      custom: (row: any) => {
        if (!row.validStartTime || !row.validEndTime) {
          return '-';
        } else {
          return `${row.validStartTime} ~ ${row.validEndTime}`;
        }
      }
    },
    {
      column: 'status',
      label: 'Approval Status',
      align: 'center',
      size: 150,
      type: 'custom',
      custom: (row: any) => {
        let value = row.status;
        if (row.approvalStatus) {
          value = row.approvalStatus;
        }
        const label = value === 'DENY' ? 'REJECT' : value;

        return (
          <div className={'flex-row flex-row-just-center'}>
            <span className={`grid-approval-status ${value}`}>{label}</span>
          </div>
        );
      }
    },
    {
      column: 'receiverNm',
      label: 'Receiver',
      align: 'center',
      size: 150
    },
    {
      column: 'issueDate',
      label: 'Issue Date',
      align: 'center',
      size: 180
    }
  ];

  const onDelete = () => {
    if (checkedList.length == 0) {
      commonMsg.alert(showAlarm, closeAlarm, 'Error', 'Please check the input value.', null);
      return;
    }

    //상태가 'draft' 인 데이터만 삭제 가능
    const filterList = checkedList.filter((row: any) => row.status !== 'DRAFT');
    if (filterList.length > 0) {
      commonMsg.alert(
        showAlarm,
        closeAlarm,
        'Warning',
        'Only "DRAFT" status can be deleted.',
        null
      );
      return;
    }

    commonMsg.confirm(
      showAlarm,
      closeAlarm,
      'Delete',
      'Would you like to Delete the Selected \n information?',
      function () {
        closeAlarm();
        const targets = checkedList.map((row: any) => row.ptwDSeq);
        ptwApi.delIssueList({ targets }).then(function (r) {
          if (r.isSuccess) {
            initSearch();
            commonMsg.alert(showAlarm, closeAlarm, 'Success', 'Deleted successfully', null);
          }
        });
      }
    );
  };

  const onRecall = () => {
    if (checkedList.length == 0) {
      commonMsg.alert(showAlarm, closeAlarm, 'Error', 'Please check the input value.', null);
      return;
    }

    //상태가 'Approval, Deny' 인 데이터만 복사 가능
    // const filterList = checkedList.filter(
    //   (row: any) => row.approvalStatus !== 'APPROVED' && row.approvalStatus !== 'DENY'
    // );
    // if (filterList.length > 0) {
    //   commonMsg.alert(
    //     showAlarm,
    //     closeAlarm,
    //     'Error',
    //     'Recall is only available when the status is Approval or Deny.',
    //     null
    //   );
    //   return;
    // }
    commonMsg.confirm(
      showAlarm,
      closeAlarm,
      'Confirm',
      'Would you like to Recall the Selected \n information?',
      () => {
        //permit 자체 복사
        const targets = checkedList.map((row: any) => row.ptwDSeq);
        ptwApi.copyIssue({ targets }).then(function (r) {
          if (r.isSuccess) {
            initSearch();
            commonMsg.alert(showAlarm, closeAlarm, 'Success', 'Recalled successfully', null);
          }
        });
      }
    );
  };

  const onIssue = () => {
    setPermitIssueList([]);
    setIsPermitIssueTypeModalOpen(true);
  };

  const onActionPermitIssueType = (selectedPermitIssueList: any) => {
    setPermitIssueList(selectedPermitIssueList);
    setIsPtwDetailModalOpen(true);
  };

  const onActionCallback = () => {
    search(1);
  };

  const initSearch = () => {
    setCurrentPage(1);
    search(1);
  };

  return (
    <StyledMyIssue>
      <PermitIssueTypeModal
        setIsModalOpen={setIsPermitIssueTypeModalOpen}
        isModalOpen={isPermitIssueTypeModalOpen}
        permitIssueList={permitIssueList}
        title={'Permit Issue'}
        onOkAction={onActionPermitIssueType}
      />
      <PermitIssueContext.Provider value={{ pageId: 'issue' }}>
        <PermitIssueDetailModal
          title={'Permit Issue'}
          setIsModalOpen={setIsPtwDetailModalOpen}
          isModalOpen={isPtwDetailModalOpen}
          isPermitIssueTypeModalOpen={isPermitIssueTypeModalOpen}
          setIsPermitIssueTypeModalOpen={setIsPermitIssueTypeModalOpen}
          permitIssueList={permitIssueList}
          setPermitIssueList={setPermitIssueList}
          detailData={detailData}
          setDetailData={setDetailData}
          onActionCallback={onActionCallback}
          isReadOnly={detailData?.status == 'DRAFT' || detailData?.status == null ? false : true}
        />
      </PermitIssueContext.Provider>
      <div className={'contents-box'}>
        <div className={'search-box'}>
          <div className={'search-item-list'}>
            <div className={'search-item date-range'}>
              <span className={'group-label'}>Date : </span>
              <div className={'group-component'}>
                <CustomDate
                  onChange={handleChange}
                  name={'startDt'}
                  value={searchData?.startDt}
                  defaultValue={searchData?.startDt}
                />
                <span>-</span>
                <CustomDate
                  onChange={handleChange}
                  name={'endDt'}
                  value={searchData?.endDt}
                  defaultValue={searchData?.endDt}
                />
              </div>
            </div>

            <div className={'search-item'}>
              <CustomSelectSingle
                label={'Permit type :'}
                labelWidth={100}
                onChange={handleChange}
                name={'formCode'}
                selectWidth={340}
                value={searchData?.formCode}
                options={[
                  { value: 'CW', label: 'Cold' },
                  { value: 'HW', label: 'Hot' },
                  { value: 'SW', label: 'Confined Space' },
                  { value: 'RC', label: 'Road Closure' }
                ]}
                nullLabel={'ALL'}
              />
            </div>

            <div className={'search-item'}>
              <CustomSelectSingle
                label={'Discipline :'}
                labelWidth={100}
                onChange={handleChange}
                name={'disciplineCd'}
                value={searchData?.disciplineCd}
                selectWidth={310}
                options={disciplineCdList}
                nullLabel={'ALL'}
              />
            </div>

            <div className={'search-item'}>
              <CustomSelectSingle
                label={'Work Risk :'}
                labelWidth={100}
                onChange={handleChange}
                name={'workRisk'}
                value={searchData?.workRisk}
                selectWidth={270}
                options={[
                  { label: 'General Work', value: 'GENERAL' },
                  { label: 'High-Risk Work', value: 'HIGH' }
                ]}
                nullLabel={'ALL'}
              />
            </div>

            <div className={'search-item'}>
              <CustomSelectSingle
                label={'Approval Status :'}
                labelWidth={130}
                onChange={handleChange}
                name={'status'}
                selectWidth={280}
                value={searchData?.status}
                options={[
                  { label: 'DRAFT', value: 'DRAFT' } /*임시저장*/,
                  { label: 'ONGOING', value: 'ONGOING' } /*ISSUE OPEN 결재 진행중*/,
                  {
                    label: 'APPROVED',
                    value: 'APPROVED'
                  } /*승인자 결재 완료(ISSUE OPEN까지 결재 완료된 상태)*/,
                  { label: 'REJECT', value: 'DENY' } /*반려*/,
                  {
                    label: 'CANCEL',
                    value: 'CANCEL'
                  } /*작성자가 퍼밋 취소(ISSUE OPEN까지 결재 완료된 상태)*/,
                  {
                    label: 'PENDING CLOSURE',
                    value: 'PENDING CLOSURE'
                  } /*작성자가 퍼밋 취소(ISSUE OPEN까지 결재 완료된 상태)*/,
                  { label: 'CLOSURE', value: 'CLOSURE' } /*CLOSING APPROVAL까지 결재 완료*/
                ]}
                nullLabel={'ALL'}
              />
            </div>

            <div className={'search-item'}>
              <CustomText
                onChange={handleChange}
                name={'searchText'}
                value={searchData?.searchText}
                isBtnClose={true}
                isBtnSearch={true}
                placeholder={'Enter search query.'}
              />
            </div>
          </div>

          <div className={'search-btn'}>
            <TextButton variant="outlined" onClick={onDelete}>
              Delete
            </TextButton>
            <TextButton variant="contained" onClick={onRecall}>
              Recall
            </TextButton>
            <TextButton variant="contained" onClick={onIssue}>
              Issue
            </TextButton>
          </div>
        </div>
        <div className={'grid-box'}>
          <CustomTable
            columns={columns}
            data={gridData}
            setData={setGridData}
            isPaging={true}
            isCheck={true}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalCount={totalCount}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            setTableSort={setTableSort}
            className={'min-w-100'}
            setSelectedRow={setSelectedRow}
            setCheckedList={setCheckedList}
            dbClick={(row) => {
              setDetailData(row);
              setIsPtwDetailModalOpen(true);
            }}
          />
        </div>
      </div>
    </StyledMyIssue>
  );
};

export default PtwMyIssue;
